import { SELF_INVITE_MESSAGE } from "./selfInvite";
import type { ApiError } from "./api";

/**
 * Maps an invite-endpoint failure to the toast the applicant sees.
 *
 * This is a lookup rather than an if-chain in the component because two
 * distinct failures share a status code: the self-invite guard and the
 * max-invitations limit are both **422**, separated only by `code`. As an
 * ordered chain, putting the status test first silently swallows the more
 * specific case and tells someone who typed their own address that they have
 * hit an invitation limit they are nowhere near. Dispatching on `code` first,
 * and falling back to status, removes the ordering hazard entirely.
 *
 * Returns null when the caller should handle it itself (the duplicate-email
 * case also triggers a refetch, so it stays in the component).
 */
export type InviteToast = { title: string; description: string; refetch: boolean };

/**
 * @param serverMaxInvitations the server's own cap (MAX_ACTIVE_INVITATIONS,
 *   currently 3), NOT the form's `maxInvitations` prop. That prop carries
 *   `requiredVouches`, which is how many vouches the org wants, a different
 *   number that would misreport the limit the server actually enforced.
 */
export function voucherInviteErrorToast(
  err: Pick<ApiError, "code" | "status">,
  serverMaxInvitations: number,
): InviteToast {
  // `code` is checked before `status` so a shared status code can never mask a
  // more specific failure.
  switch (err.code) {
    case "SELF_INVITE":
      return { title: "Use a different address", description: SELF_INVITE_MESSAGE, refetch: false };
    case "DUPLICATE_EMAIL":
      return {
        title: "Already invited",
        description: "This email already has an active invitation.",
        // Server says this email is on file but our cached list may have
        // dropped it, so refetch to make the row reappear.
        refetch: true,
      };
  }

  if (err.status === 409) {
    return {
      title: "Already invited",
      description: "This email already has an active invitation.",
      refetch: true,
    };
  }

  if (err.status === 422) {
    return {
      title: "Limit reached",
      description: `You can have at most ${serverMaxInvitations} active invitations.`,
      refetch: true,
    };
  }

  return { title: "Could not send", description: (err as { message?: string }).message ?? "Please try again.", refetch: false };
}
